import React, { createContext, useContext, useState, useEffect } from 'react';
import logger from '../utils/logger';

const CookieContext = createContext();

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies must be used within a CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showConsentBanner, setShowConsentBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Types de cookies avec leurs descriptions
  const cookieTypes = {
    essential: {
      name: 'Cookies essentiels',
      description: 'Nécessaires au fonctionnement du site (authentification, sécurité, navigation)',
      required: true,
      enabled: true
    },
    analytics: {
      name: 'Cookies analytiques',
      description: 'Nous aident à comprendre comment vous utilisez notre site (Google Analytics)',
      required: false,
      enabled: false
    },
    marketing: {
      name: 'Cookies marketing',
      description: 'Utilisés pour personnaliser la publicité et mesurer son efficacité',
      required: false,
      enabled: false
    },
    preferences: {
      name: 'Cookies de préférences',
      description: 'Mémorisent vos préférences (langue, région, paramètres d\'affichage)',
      required: false,
      enabled: false
    }
  };

  const [cookiePreferences, setCookiePreferences] = useState(cookieTypes);

  // Charger les préférences depuis localStorage au montage
  useEffect(() => {
    const savedConsent = localStorage.getItem('asar_cookie_consent');
    const savedPreferences = localStorage.getItem('asar_cookie_preferences');

    if (savedConsent) {
      const consent = JSON.parse(savedConsent);
      setCookieConsent(consent);
      
      if (savedPreferences) {
        const preferences = JSON.parse(savedPreferences);
        setCookiePreferences(preferences);
      }
    } else {
      // Première visite - afficher le banner
      setShowConsentBanner(true);
    }
  }, []);

  // Sauvegarder les préférences
  const savePreferences = (preferences) => {
    setCookiePreferences(preferences);
    localStorage.setItem('asar_cookie_preferences', JSON.stringify(preferences));
    
    const consent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      preferences: preferences
    };
    
    setCookieConsent(consent);
    localStorage.setItem('asar_cookie_consent', JSON.stringify(consent));
    setShowConsentBanner(false);
    setShowPreferences(false);

    // Le consentement cookies sert aussi de geste utilisateur pour débloquer le son de
    // l'intro DNA-24 : le son est activé si l'utilisateur a accepté au moins un cookie non essentiel.
    const soundAllowed = Object.values(preferences).some((p) => !p.required && p.enabled);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('dna-cookie-resolved', { detail: { sound: soundAllowed } }));
    }

    // Appliquer les cookies selon les préférences
    applyCookieSettings(preferences);
  };

  // Accepter tous les cookies
  const acceptAllCookies = () => {
    const allAccepted = { ...cookieTypes };
    Object.keys(allAccepted).forEach(key => {
      if (!allAccepted[key].required) {
        allAccepted[key].enabled = true;
      }
    });
    savePreferences(allAccepted);
  };

  // Refuser tous les cookies non-essentiels
  const rejectAllCookies = () => {
    const onlyEssential = { ...cookieTypes };
    Object.keys(onlyEssential).forEach(key => {
      if (!onlyEssential[key].required) {
        onlyEssential[key].enabled = false;
      }
    });
    savePreferences(onlyEssential);
  };

  // Appliquer les paramètres de cookies
  const applyCookieSettings = (preferences) => {
    // Google Analytics
    if (preferences.analytics?.enabled) {
      // Initialiser Google Analytics
      logger.debug('✅ Google Analytics activé');
    } else {
      // Désactiver Google Analytics
      logger.debug('❌ Google Analytics désactivé');
    }

    // Cookies marketing
    if (preferences.marketing?.enabled) {
      logger.debug('✅ Cookies marketing activés');
    } else {
      logger.debug('❌ Cookies marketing désactivés');
    }

    // Cookies de préférences
    if (preferences.preferences?.enabled) {
      logger.debug('✅ Cookies de préférences activés');
    } else {
      logger.debug('❌ Cookies de préférences désactivés');
    }
  };

  // Ouvrir les préférences
  const openPreferences = () => {
    setShowPreferences(true);
  };

  // Réinitialiser le consentement (pour tests)
  const resetConsent = () => {
    localStorage.removeItem('asar_cookie_consent');
    localStorage.removeItem('asar_cookie_preferences');
    setCookieConsent(null);
    setCookiePreferences(cookieTypes);
    setShowConsentBanner(true);
  };

  const value = {
    cookieConsent,
    cookiePreferences,
    setCookiePreferences,
    showConsentBanner,
    setShowConsentBanner,
    showPreferences,
    setShowPreferences,
    acceptAllCookies,
    rejectAllCookies,
    savePreferences,
    openPreferences,
    resetConsent,
    cookieTypes
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};